import { useState, useEffect, useRef, useCallback, useMemo } from "react";
import { useParams } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";

import TimeEntryEditForm from "./TimeEntryEditForm";
import UserIcon from "../ui/icons/UserIcon";
import Modal from "../../lib/Modal";

import useAbortSignal from "../../hooks/useAbortSignal";
import fetchWrapper from "../../lib/apiCall";
import { useTimeTracking } from "../../context/TimeTrackingContext";
import { errorToast } from "../../util/toastNotifications";
import { useAppData } from "../../context/AppDataContext";
import { useAuthInfo } from "../../context/AuthContext";

import calendarIcon from "../../assets/images/svg/calendar-icon.svg";

const TimeEntryForm = ({ assignedUsers, taskId: taskIdProp }) => {
  const [assignees, setAssignees] = useState([]);
  const [selectedAssignee, setSelectedAssignee] = useState(null);
  const [currentTimeRecords, setCurrentTimeRecords] = useState([]);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [recordToEdit, setRecordToEdit] = useState("");
  const [isSaving, setIsSaving] = useState(false);
  const [deleteDebounce, setDeleteDebounce] = useState(0);
  const [showBillableMessage, setShowBillableMessage] = useState(false);
  const [newRecord, setNewRecord] = useState({
    newStart: getCurrentTime(),
    newEnd: getCurrentTime(),
    newDate: getCurrentDate(),
    newIsBillable: true,
  });

  const { taskId: taskIdFromParams } = useParams();
  const taskId = taskIdProp || taskIdFromParams;

  const { signal } = useAbortSignal();
  const { userInfo } = useAuthInfo();
  const { allAssignees = [] } = useAppData();
  const {
    isAddingTime,
    timeEntries,
    groupedTimeEntries,
    fetchTimeEntries,
    handleDataEntry,
    handleCalculateSingleEntryTime,
    handleCalculateTotalTaskTime,
    handleActivityData,
  } = useTimeTracking();

  const emptyTimeRecord = useRef([
    <div className="empty-record-wrapper" key="0">
      No Tracked Time
    </div>,
  ]);

  const handleCalculateTotalTime = (userId, isBillable) => {
    let result = "00:00:00";

    if (timeEntries && timeEntries.length > 0) {
      let recordArr = timeEntries.filter((entry) => entry.user_id === userId);

      if (recordArr && recordArr.length > 0) {
        if (isBillable === "billable")
          recordArr = recordArr.filter((entry) => entry.billable);
        if (isBillable === "non-billable")
          recordArr = recordArr.filter((entry) => !entry.billable);

        const secondsArr = recordArr.reduce((acc, cV) => {
          const start_date = new Date(cV.date_started);
          const end_date = new Date(cV.date_ended);

          let seconds_diff = (end_date.getTime() - start_date.getTime()) / 1000;

          acc.push(seconds_diff);
          return acc;
        }, []);

        const totalSeconds = secondsArr.reduce((acc, cV) => acc + cV, 0);

        const totalMinutes = Math.floor(totalSeconds / 60);

        let seconds = Math.floor(totalSeconds % 60);
        let hours = Math.floor(totalMinutes / 60);
        let minutes = totalMinutes % 60;

        if (hours < 10) hours = `0${hours}`;
        if (minutes < 10) minutes = `0${minutes}`;
        if (seconds < 10) seconds = `0${seconds}`;

        result = `${hours}:${minutes}:${seconds}`;
      }
    }
    return result;
  };

  function getCurrentTime() {
    const now = new Date();
    let hours = now.getHours();
    const minutes = now.getMinutes().toString().padStart(2, "0");

    const ampm = hours >= 12 ? "pm" : "am";
    hours = hours % 12 || 12;

    return `${hours}:${minutes} ${ampm}`;
  }

  function getCurrentDate() {
    const now = new Date();
    const year = now.getFullYear();
    const month = String(now.getMonth() + 1).padStart(2, "0");
    const day = String(now.getDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
  }

  const uniqueUsers = useMemo(() => {
    const map = new Map();

    timeEntries.forEach((entry) => {
      if (!map.has(entry.user_id)) {
        map.set(entry.user.user_id, {
          user_id: entry.user.user_id,
          first_name: entry.user.first_name,
          last_name: entry.user.last_name,
        });
      }
    });

    return Array.from(map.values());
  }, [timeEntries]);

  const renderAssignees = () => {
    const mappedAssignees = uniqueUsers.map((assignee) => {
      const firstName = assignee.first_name || "";
      const lastName = assignee.last_name || "";
      const userInitials = `${firstName[0] || ""}${lastName[0] || ""}`;

      return (
        <div className="assignee-wrapper" key={assignee.user_id}>
          <div
            className={`assignee-inner-wrapper ${
              selectedAssignee?.user_id === assignee.user_id ? "active" : ""
            } ${selectedAssignee ? "one-selected" : ""}`}
            onClick={() => handleSelect(assignee)}
          >
            <UserIcon initials={userInitials} iconColor={assignee.color} />

            <div className="assignee-name">
              {assignee.first_name || ""} {assignee.last_name || ""}
            </div>

            <div className="assignee-non-billable">
              {handleCalculateTotalTime(assignee.user_id, "non-billable")}
            </div>

            <div className="assignee-billable">
              {handleCalculateTotalTime(assignee.user_id, "billable")}
            </div>
          </div>

          <div className="assignee-record-container">
            {selectedAssignee && selectedAssignee?.user_id === assignee.user_id
              ? currentTimeRecords
              : null}
            {selectedAssignee &&
            selectedAssignee?.user_id === assignee.user_id &&
            currentTimeRecords.length === 0
              ? emptyTimeRecord.current
              : null}
          </div>
        </div>
      );
    });

    mappedAssignees.push(
      <div className="assignee-total-task-time-wrapper" key="total-task-time">
        <div className="assignee-inner-wrapper">
          <div className="assignee-name">Total Tracked Time</div>

          <div className="assignee-non-billable">
            {handleCalculateTotalTaskTime("non-billable")}
          </div>

          <div className="assignee-billable">
            {handleCalculateTotalTaskTime("billable")}
          </div>
        </div>
      </div>
    );
    return mappedAssignees;
  };

  const handleSelect = (assignee) => {
    setCurrentTimeRecords([]);
    if (isAddingTime) {
      handleDataEntry("isAddingTime", false);
    }
    setSelectedAssignee((prev) => {
      if (prev) {
        if (prev.user_id === assignee.user_id) {
          return null;
        } else {
          return assignee;
        }
      } else {
        return assignee;
      }
    });
  };

  const handleSave = (e) => {
    e.preventDefault();

    setIsSaving(true);
  };

  const handleDelete = useCallback(
    (entryId) => {
      if (deleteDebounce === 0) {
        if (signal?.aborted) {
          return;
        }

        fetchWrapper
          .apiCall(
            `/time-entry/delete/${entryId}`,
            "DELETE",
            null,
            null,
            signal
          )
          .then((data) => {
            if (data?.activity_log) {
              handleActivityData(data.activity_log);
            } else if (data?.results) {
              handleActivityData(data.results);
            }
            return fetchTimeEntries();
          })
          .catch((err) => {
            if (err.name === "AbortError" || err.message?.includes("aborted")) {
              return;
            }
            console.error("Time Entry delete error: ", err);
            errorToast("Failed to delete time entry");
          });
      }
    },
    [handleActivityData, fetchTimeEntries, signal, deleteDebounce]
  );

  const handleDeleteDebounce = useCallback(
    (entryId) => {
      setDeleteDebounce((prev) => ++prev);
      return handleDelete(entryId);
    },
    [handleDelete]
  );

  const handleCheckboxChange = (e) => {
    setNewRecord((prev) => ({ ...prev, newIsBillable: e.target.checked }));

    setShowBillableMessage(!e.target.checked);
  };

  const handleClose = () => {
    setRecordToEdit("");
    return setIsEditModalOpen(false);
  };

  useEffect(() => {
    let deleteTimeout;
    if (deleteDebounce !== 0) {
      deleteTimeout = setTimeout(() => {
        setDeleteDebounce(0);
      }, 1000);
    }

    return () => clearTimeout(deleteTimeout);
  }, [deleteDebounce]);

  const computedAssignees = useMemo(() => {
    let filteredAssignedUsers = [];
    let assignedUserIds = [];

    if (assignedUsers && assignedUsers.length > 0) {
      filteredAssignedUsers = assignedUsers.filter(
        (user) => user.user_id !== userInfo?.user_id
      );
    }

    if (filteredAssignedUsers.length > 0) {
      assignedUserIds = assignedUsers.map((user) => user.user_id);
    }

    if (
      allAssignees &&
      allAssignees.length > 0 &&
      groupedTimeEntries &&
      Object.keys(groupedTimeEntries).length > 0
    ) {
      const taskUsers = Object.keys(groupedTimeEntries);

      const users = allAssignees.filter(
        (user) =>
          user &&
          user.user_id &&
          (user.first_name || user.last_name) &&
          (taskUsers.includes(user.user_id) ||
            user.user_id === userInfo?.user_id ||
            assignedUserIds.includes(user.user_id))
      );

      return users;
    } else {
      const validFilteredUsers = filteredAssignedUsers.filter(
        (user) => user && user.user_id && (user.first_name || user.last_name)
      );
      return userInfo ? [userInfo, ...validFilteredUsers] : validFilteredUsers;
    }
  }, [allAssignees, groupedTimeEntries, userInfo?.user_id, assignedUsers]);

  const prevAssigneeIdsRef = useRef(null);

  useEffect(() => {
    const currentAssigneeIds = computedAssignees
      .map((u) => u?.user_id)
      .filter(Boolean)
      .sort()
      .join(",");
    const prevAssigneeIds = prevAssigneeIdsRef.current;

    if (currentAssigneeIds !== prevAssigneeIds) {
      prevAssigneeIdsRef.current = currentAssigneeIds;
      setAssignees(computedAssignees);
    }
  }, [computedAssignees]);

  useEffect(() => {
    if (!isEditModalOpen) setRecordToEdit("");
  }, [isEditModalOpen]);

  useEffect(() => {
    if (isSaving && !selectedAssignee) {
      setIsSaving(false);
    }
  }, [isSaving, selectedAssignee]);

  useEffect(() => {
    if (!isAddingTime) {
      fetchTimeEntries();
      setNewRecord({
        newStart: getCurrentTime(),
        newEnd: getCurrentTime(),
        newDate: getCurrentDate(),
        newIsBillable: true,
      });
    }
  }, [isAddingTime, fetchTimeEntries]);

  useEffect(() => {
    if (recordToEdit) {
      setIsEditModalOpen(true);
    }
  }, [recordToEdit]);

  useEffect(() => {
    let currentRecords = [];
    if (isAddingTime) {
      setSelectedAssignee(
        assignees.filter((assignee) => assignee.user_id === userInfo.user_id)[0]
      );
    }
    if (selectedAssignee) {
      const currentTimeEntries = timeEntries.filter(
        (rec) => rec.user_id === selectedAssignee.user_id
      );

      currentRecords = currentTimeEntries.map((record) => {
        return (
          <div className="time-record-wrapper" key={record.time_entry_id}>
            <div className="time">{handleCalculateSingleEntryTime(record)}</div>

            <div className="middle-wrapper">
              <div className="entry-date">
                <FontAwesomeIcon
                  icon="fa-solid fa-calendar-day"
                  className="calendar-icon"
                />
                {record.date_started.split("T")[0]}
              </div>

              <div
                className={`billable-status ${
                  record.billable ? "billable" : "non-billable"
                }`}
              >
                {record.billable ? "Billable" : "Non-Billable"}
              </div>
            </div>

            {userInfo.user_id === record.user_id ? (
              <div className="buttons-wrapper">
                <FontAwesomeIcon
                  icon="fa-solid fa-pen"
                  className="edit-icon"
                  onClick={() => setRecordToEdit(record.time_entry_id)}
                />

                <FontAwesomeIcon
                  icon="fa-solid fa-trash"
                  className="trash-icon"
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    handleDeleteDebounce(record.time_entry_id);
                  }}
                />
              </div>
            ) : (
              <div className="buttons-wrapper disabled">
                <FontAwesomeIcon icon="fa-solid fa-pen" className="edit-icon" />

                <FontAwesomeIcon
                  icon="fa-solid fa-trash"
                  className="trash-icon"
                />
              </div>
            )}
          </div>
        );
      });
    }
    if (isAddingTime) {
      const newRecordElement = (
        <div className="new-time-record-wrapper" key="0">
          <input
            className="time"
            type="text"
            name="start-time"
            maxLength="8"
            value={newRecord.newStart}
            placeholder="00:00 am"
            onChange={(e) =>
              setNewRecord((prev) => ({ ...prev, newStart: e.target.value }))
            }
          />
          -
          <input
            className="time"
            type="text"
            name="end-time"
            maxLength="8"
            value={newRecord.newEnd}
            placeholder="00:00 am"
            onChange={(e) =>
              setNewRecord((prev) => ({ ...prev, newEnd: e.target.value }))
            }
          />
          <div className="label-input-wrapper">
            <div className="date-input-wrapper">
              <input
                className="date-input"
                type="date"
                value={newRecord.newDate}
                onChange={(e) =>
                  setNewRecord((prev) => ({ ...prev, newDate: e.target.value }))
                }
              />
            </div>

            <img
              className="calendar-icon"
              src={calendarIcon}
              alt="calendar icon"
            />
          </div>
          <div className="checkbox-wrapper" title="Billable ?">
            <input
              className="checkbox"
              type="checkbox"
              id="billable-checkbox"
              defaultChecked={true}
              onChange={handleCheckboxChange}
              title="Billable ?"
            />

            <label className="checkbox-label" htmlFor="billable-checkbox">
              Is Billable
            </label>
          </div>
        </div>
      );
      setCurrentTimeRecords([newRecordElement]);
    } else {
      setCurrentTimeRecords(currentRecords);
    }
  }, [
    selectedAssignee,
    isAddingTime,
    newRecord,
    assignees,
    timeEntries,
    handleDelete,
    handleDeleteDebounce,
    userInfo.user_id,
    handleCalculateSingleEntryTime,
  ]);

  useEffect(() => {
    if (isSaving) {
      if (!newRecord.newStart || !newRecord.newEnd || !newRecord.newDate) {
        errorToast("Please fill in all time fields");
        setIsSaving(false);
        return;
      }

      if (!selectedAssignee?.user_id) {
        errorToast("Please select an assignee");
        setIsSaving(false);
        return;
      }

      if (newRecord.newStart && newRecord.newEnd && newRecord.newDate) {
        let newRecordStart = newRecord.newStart;
        let newRecordEnd = newRecord.newEnd;

        newRecordStart = newRecordStart.toLowerCase().trim();
        newRecordEnd = newRecordEnd.toLowerCase().trim();

        const validStartTimeOfDay =
          newRecordStart.includes("am") || newRecordStart.includes("pm");
        const validEndTimeOfDay =
          newRecordEnd.includes("am") || newRecordEnd.includes("pm");

        let startDateTime = "";
        let endDateTime = "";

        let validStartHour = true;
        let validEndHour = true;
        let validStartMinute = true;
        let validEndMinute = true;
        let validTimeRange = true;

        if (validStartTimeOfDay && validEndTimeOfDay) {
          const startTimeOfDay = newRecordStart.slice(-2).toLowerCase();
          const endTimeOfDay = newRecordEnd.slice(-2).toLowerCase();

          const startTimeStr = newRecordStart.slice(0, -2).trim();
          const endTimeStr = newRecordEnd.slice(0, -2).trim();

          const startTimeParts = startTimeStr.split(":");
          const endTimeParts = endTimeStr.split(":");

          if (startTimeParts.length < 2 || endTimeParts.length < 2) {
            validStartHour = false;
            validEndHour = false;
          } else {
            let startHour = parseInt(startTimeParts[0], 10);
            let startMinute = parseInt(startTimeParts[1], 10);
            let startSecond =
              startTimeParts.length > 2 ? parseInt(startTimeParts[2], 10) : 0;

            let endHour = parseInt(endTimeParts[0], 10);
            let endMinute = parseInt(endTimeParts[1], 10);
            let endSecond =
              endTimeParts.length > 2 ? parseInt(endTimeParts[2], 10) : 0;

            if (startHour < 1 || startHour > 12) validStartHour = false;
            if (endHour < 1 || endHour > 12) validEndHour = false;

            if (startMinute < 0 || startMinute > 59) validStartMinute = false;
            if (endMinute < 0 || endMinute > 59) validEndMinute = false;

            if (startSecond < 0 || startSecond > 59) validStartMinute = false;
            if (endSecond < 0 || endSecond > 59) validEndMinute = false;

            if (startTimeParts[1].length === 1) validStartMinute = false;
            if (endTimeParts[1].length === 1) validEndMinute = false;

            if (startTimeOfDay === "pm" && startHour < 12) {
              startHour = startHour + 12;
            } else if (startTimeOfDay === "am" && startHour === 12) {
              startHour = 0;
            }

            if (endTimeOfDay === "pm" && endHour < 12) {
              endHour = endHour + 12;
            } else if (endTimeOfDay === "am" && endHour === 12) {
              endHour = 0;
            }

            const formattedStartHour =
              startHour < 10 ? `0${startHour}` : `${startHour}`;
            const formattedEndHour =
              endHour < 10 ? `0${endHour}` : `${endHour}`;
            const formattedStartMinute =
              startMinute < 10 ? `0${startMinute}` : `${startMinute}`;
            const formattedEndMinute =
              endMinute < 10 ? `0${endMinute}` : `${endMinute}`;
            const formattedStartSecond =
              startSecond < 10 ? `0${startSecond}` : `${startSecond}`;
            const formattedEndSecond =
              endSecond < 10 ? `0${endSecond}` : `${endSecond}`;

            const localStartDateTime = `${newRecord.newDate}T${formattedStartHour}:${formattedStartMinute}:${formattedStartSecond}`;
            const localEndDateTime = `${newRecord.newDate}T${formattedEndHour}:${formattedEndMinute}:${formattedEndSecond}`;

            const localStartDate = new Date(localStartDateTime);
            const localEndDate = new Date(localEndDateTime);

            startDateTime = localStartDate.toISOString();
            endDateTime = localEndDate.toISOString();

            const compareStart = new Date(startDateTime).getTime();
            const compareEnd = new Date(endDateTime).getTime();

            if (compareStart > compareEnd) validTimeRange = false;
          }
        }

        if (
          startDateTime &&
          endDateTime &&
          validStartHour &&
          validEndHour &&
          validStartMinute &&
          validEndMinute &&
          validTimeRange &&
          selectedAssignee?.user_id
        ) {
          const body = {
            org_id: userInfo.org_id,
            user_id: selectedAssignee.user_id,
            entered_by_id: userInfo.user_id,
            task_id: taskId,
            date_started: startDateTime,
            date_ended: endDateTime,
            billable: newRecord.newIsBillable,
          };

          if (signal?.aborted) {
            setIsSaving(false);
            return;
          }

          fetchWrapper
            .apiCall("/time-entry", "POST", body, null, signal)
            .then((data) => {
              if (data?.activity_log) {
                handleActivityData(data.activity_log);
              }
              fetchTimeEntries();
              setIsSaving(false);
              handleDataEntry("isAddingTime", false);
            })
            .catch((err) => {
              if (
                err.name === "AbortError" ||
                err.message?.includes("aborted")
              ) {
                return;
              }
              console.error("Time Entries ADD error: ", err);
              errorToast("Failed to add time entry");
              setIsSaving(false);
            });
        } else {
          errorToast("Invalid time input for time entry");
          setIsSaving(false);
        }
      } else {
        setIsSaving(false);
      }
    }
  }, [
    newRecord,
    isSaving,
    selectedAssignee,
    userInfo.user_id,
    userInfo.org_id,
    taskId,
    signal,
    handleDataEntry,
    handleActivityData,
    fetchTimeEntries,
  ]);

  return (
    <div className="time-entry-form-container">
      <div className="header-fields-wrapper">
        <div className="header-assignee">Assignee</div>

        <div className="header-non-billable">Non-Billable</div>

        <div className="header-billable-wrapper">
          <div className="header-billable">Billable</div>

          <div className="header-dollar-sign">
            <FontAwesomeIcon icon="fa-solid fa-dollar-sign" />
          </div>
        </div>
      </div>

      <div className="assignees-container">{renderAssignees()}</div>

      {!isAddingTime ? (
        <div
          className="add-btn-wrapper"
          onClick={() => handleDataEntry("isAddingTime", true)}
        >
          <div className="add-btn-text">+ Time</div>

          {showBillableMessage ? (
            <div className="billable-message">
              This timestamp will save to{" "}
              <span className="highlight">Non-Billable</span>
            </div>
          ) : null}
        </div>
      ) : (
        <div className="save-btn-wrapper" onClick={handleSave}>
          <div className="save-btn-text">Save</div>

          {showBillableMessage ? (
            <div className="billable-message">
              This timestamp will save to{" "}
              <span className="highlight">Non-Billable</span>
            </div>
          ) : null}
        </div>
      )}

      <Modal
        isOpen={isEditModalOpen}
        onRequestClose={handleClose}
        doNotEditOverflow
        overlay={{
          backdropFilter: "none",
          backgroundColor: "transparent",
          zIndex: 2000,
        }}
        content={{
          border: "none",
          height: "180px",
          width: "240px",
          left: "calc(100vw - 610px)",
          top: "235px",
          borderRadius: "15px",
        }}
      >
        <TimeEntryEditForm
          setIsEditModalOpen={setIsEditModalOpen}
          setRecordToEdit={setRecordToEdit}
          recordCheck={recordToEdit}
          fetchTimeEntries={fetchTimeEntries}
          record={
            recordToEdit
              ? timeEntries.filter(
                  (entry) => entry.time_entry_id === recordToEdit
                )[0]
              : ""
          }
        />
      </Modal>
    </div>
  );
};

export default TimeEntryForm;
